import {
	type Analytics,
	AnalyticsEventActions,
	AnalyticsEventSource,
} from '../components/AnalyticsContext';
import { type TeamListItemUiElement, type TeamWithMemberships } from '../model/team';
import { useOrg } from '../state/org';

import { logErrorMessage } from './sentry';

interface LegionV3OrgInfo {
	orgId?: string;
	isOrgLoading: boolean;
}

export function useV3OrgIdStatus(): LegionV3OrgInfo {
	const [{ orgId }] = useOrg();

	return {
		orgId,
		isOrgLoading: !orgId,
	};
}

/**
 * This function helps to reduce manual testing jobs. So when OrgID-related endpoints are stable in future, we won't need this function anymore.
 * We expect there is 0 (Sentry and Operational Analytics) events trigged from this function. If there is any events triggered,
 * that means users can see team from different Org (very bad situation).

 * @param teams: list of teams data we want to check `orgID` of each team.
 * @param analytics
 * @param queryName
 * @param currentOrgId: Org ID was fetched from AGG in the stand-alone app or passed from products.
 */
export function verifyTeamsInSameOrgIfNeeded<T extends TeamListItemUiElement | TeamWithMemberships>(
	teams: T[],
	analytics: Analytics,
	queryName: 'teamsOfUser' | 'teamSearch',
	currentOrgId?: string,
) {
	if (!currentOrgId) {
		return;
	}

	const orgARI = `ari:cloud:platform::org/${currentOrgId}`;

	const invalidTeams = teams.filter(
		(team) => !team.includesYou && team.orgId !== currentOrgId && team.orgId !== orgARI,
	);

	if (invalidTeams.length) {
		const logTeamsData = invalidTeams.map((t) => ({
			teamId: t.id,
			orgId: t.orgId,
		}));

		analytics.pushOperationalEvent({
			source: AnalyticsEventSource.CLIENT_METRICS,
			actionSubject: 'TeamOrgMismatch',
			action: AnalyticsEventActions.FAILED,
			attributes: {
				currentOrgId,
				queryName,
				// maybe we don't need to log `logTeamsData` here in attributes here,
				// we can use Sentry to find the data.
			},
		});
		logErrorMessage('TeamOrgMismatch', {
			currentOrgId,
			teams: logTeamsData,
		});
	}
}
